import { BranchQuoteSupportItemType, BranchQuoteSupportStatus } from '../../../api/dealer-api-interface-franchisee';
import { branchUsersCache } from '../../cache-impl/branch-users-cache';
import { customElement, state } from 'lit/decorators.js';
import { EventTemplate } from '../../../../webmodule-common/interop/webmodule-interop';
import { getInternalId } from '../../../../webmodule-common/other/ui/databinding/databinding';
import { html } from 'lit';
import { TabManager } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { QuoteSupportInboxTable } from './quote-support-inbox-table';
import { LangSupportInboxView } from '../../../../localization/scripts/dealer-franchisee/support-inbox/views/support-inbox-view-lang';
import { userDataStore } from '../../common/current-user-data-store';
import { ViewBase } from '../../../../webmodule-common/other/ui/view-base';

@customElement('wm-supportinboxview')
export class SupportInboxView extends ViewBase {
  @state()
  protected viewAll?: QuoteSupportInboxTable;
  @state()
  protected viewReviews?: QuoteSupportInboxTable;
  @state()
  protected viewSupport?: QuoteSupportInboxTable;
  @state()
  protected viewCustomItems?: QuoteSupportInboxTable;
  @state()
  protected viewArchived?: QuoteSupportInboxTable;

  tabManager: TabManager;
  elementId: string;

  private userCache = branchUsersCache();

  constructor() {
    super();
    this.elementId = `quote-support-inbox-view-${getInternalId()}`;

    this.createTables();
    this.tabManager = this.createTabManager();
  }

  public async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    await this.userCache.prefetch([userDataStore.defaultBranch.id]);
  }

  public createTables(): void {
    const activeStates =
      // eslint-disable-next-line no-bitwise
      BranchQuoteSupportStatus.Active |
      BranchQuoteSupportStatus.ReActivated |
      BranchQuoteSupportStatus.New |
      BranchQuoteSupportStatus.Overdue;

    const getBranchUserIds = () =>
      this.userCache.getUsersForBranch(userDataStore.defaultBranch.id)?.map(x => x.id) ?? [];

    this.viewAll = new QuoteSupportInboxTable({
      tabName: 'all',
      caption: () => LangSupportInboxView.currentAll(),
      statusFilter: activeStates,
      typeFilter: BranchQuoteSupportItemType.All,
      getBranchUsers: getBranchUserIds
    });
    this.viewReviews = new QuoteSupportInboxTable({
      tabName: 'quote-reviews',
      caption: () => LangSupportInboxView.quoteReviews(),
      statusFilter: activeStates,
      typeFilter: BranchQuoteSupportItemType.QuoteReview,
      getBranchUsers: getBranchUserIds,
      columnOptions: { hideRequestTypeColumn: true }
    });
    this.viewSupport = new QuoteSupportInboxTable({
      tabName: 'support-request',
      caption: () => LangSupportInboxView.supportRequests(),
      statusFilter: activeStates,
      typeFilter: BranchQuoteSupportItemType.QuoteAssistanceRequest,
      getBranchUsers: getBranchUserIds,
      columnOptions: { hideRequestTypeColumn: true }
    });
    this.viewCustomItems = new QuoteSupportInboxTable({
      tabName: 'special-item-request',
      caption: () => LangSupportInboxView.specialItemRequests(),
      statusFilter: activeStates,
      typeFilter: BranchQuoteSupportItemType.EngineeredToOrder,
      getBranchUsers: getBranchUserIds,
      columnOptions: { hideRequestTypeColumn: true }
    });
    this.viewArchived = new QuoteSupportInboxTable({
      tabName: 'archived-items',
      caption: () => LangSupportInboxView.archived(),
      // eslint-disable-next-line no-bitwise
      statusFilter: BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved,
      typeFilter: BranchQuoteSupportItemType.All,
      getBranchUsers: getBranchUserIds,
      columnOptions: { showResolvedColumns: true, hideLastModifiedColumn: true }
    });
  }

  protected getInitialPages(): TabPage[] {
    const list: TabPage[] = [];

    if (this.viewAll) list.push(this.viewAll.createTabPage());
    if (this.viewReviews) list.push(this.viewReviews.createTabPage());
    if (this.viewSupport) list.push(this.viewSupport.createTabPage());
    if (this.viewCustomItems) list.push(this.viewCustomItems.createTabPage());
    if (this.viewArchived) list.push(this.viewArchived.createTabPage());
    return list;
  }

  createTabManager(): TabManager {
    // build static pages for each of the configured table settings
    return new TabManager({
      initialIndex: 0,
      pages: () => this.getInitialPages()
    });
  }

  async prepareForSave(): Promise<void> {
    //DOES NOTHING HANDLED BY CHILDREN
  }

  public internalDataChanged(): boolean {
    return false;
  }

  protected template(): EventTemplate {
    return html` <div id=${this.elementId} class="page-content">${this.tabManager.ui}</div>`;
  }

  public async setActiveTabByHash() {
    await this.tabManager?.applyWindowHash();
  }
}
